import { useState } from 'react';
import { Sparkles, Zap } from 'lucide-react';
import Toggle from './Toggle';
import { aiService } from '../../services/settings/ai.service';
import type { AutomationRule } from '../../types/settings.types';

interface AutomationPanelProps {
  /** Only show rules in this category; omit to show all. */
  category?: AutomationRule['category'];
  title?: string;
  description?: string;
}

/**
 * "Smart Automation" surface. Lists AI-managed and manual rules with live
 * toggles. Backed by the mock ai.service today; swap for callables later.
 */
export default function AutomationPanel({
  category,
  title = 'Smart Automation',
  description = 'AI-assisted rules that adapt Messego to your context',
}: AutomationPanelProps) {
  // The rule set is synchronous and fixed per category, so seed it lazily once.
  const [rules, setRules] = useState<AutomationRule[]>(() => {
    const all = aiService.getAutomationRules();
    return category ? all.filter((r) => r.category === category) : all;
  });

  const toggle = async (id: string, enabled: boolean) => {
    setRules((rs) => rs.map((r) => (r.id === id ? { ...r, enabled } : r)));
    await aiService.setAutomationEnabled(id, enabled);
  };

  if (rules.length === 0) return null;

  return (
    <section className="bg-gradient-to-br from-neutral-900 to-neutral-800 text-white rounded-3xl shadow-sm dark:shadow-none overflow-hidden">
      <header className="flex items-start gap-3 px-6 pt-6 pb-4">
        <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center">
          <Sparkles className="w-5 h-5" />
        </div>
        <div>
          <h2 className="font-bold">{title}</h2>
          <p className="text-sm text-white/60 mt-0.5">{description}</p>
        </div>
      </header>
      <div className="px-3 pb-3 space-y-1">
        {rules.map((rule) => (
          <div key={rule.id} className="flex items-center justify-between gap-4 rounded-2xl px-3 py-3 hover:bg-white/5 transition-colors">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-sm truncate">{rule.name}</p>
                {rule.aiManaged && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide bg-white/10 text-white/80 rounded-full px-2 py-0.5">
                    <Zap className="w-3 h-3" /> AI
                  </span>
                )}
              </div>
              <p className="text-xs text-white/50 mt-0.5 line-clamp-2">{rule.description}</p>
              <p className="text-[11px] text-white/40 mt-1">
                <span className="text-white/60">When</span> {rule.trigger} · <span className="text-white/60">then</span> {rule.action}
              </p>
            </div>
            <Toggle checked={rule.enabled} onChange={(v) => toggle(rule.id, v)} label={rule.name} />
          </div>
        ))}
      </div>
    </section>
  );
}
